/**
 * TavernFloor — top-down social view of the room. Per
 * docs/ui/tavern-floor-chat.md, this is now the SOCIAL HEART of the
 * waiting state: walkers show readiness/host/disconnected status,
 * chat messages render as speech bubbles above the speaker, reactions
 * pop as single-glyph bubbles, and the chat composer docks to the
 * bottom of the floor view so it's always reachable on mobile (no
 * scroll required).
 *
 * Position updates run on rAF and mutate DOM directly via refs (no
 * React re-render per frame, no CSS transition fight). Bubble state
 * is React-driven because they spawn / despawn at lower cadence.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LobbyView } from '@tt/lobby';
import { IconAvatar } from '../characters/IconAvatar';
import { titleRarityClass } from '../me/titleRarity';
import type { ChatMsg, Reaction } from './useLobbySocket';

type Player = LobbyView['players'][number];

interface Spot {
  id: string;
  label: string;
  x: number; // % across the floor
  y: number; // % down the floor
  emoji: string;
}

const SPOTS: Spot[] = [
  { id: 'bar',       label: 'the bar',    x: 16, y: 30, emoji: '🍻' },
  { id: 'hearth',    label: 'the hearth', x: 50, y: 22, emoji: '🔥' },
  { id: 'window',    label: 'the window', x: 84, y: 30, emoji: '🌙' },
  { id: 'billiards', label: 'the table',  x: 32, y: 70, emoji: '🎯' },
  { id: 'corner',    label: 'the corner', x: 70, y: 75, emoji: '🪑' },
];

interface AvatarSim {
  player: Player;
  x: number; y: number;
  targetX: number; targetY: number;
  dwell: number;
  hash: number;
  facingFlip: 1 | -1;
}

interface Bubble {
  /** Unique key for React. */
  id: string;
  /** Owner — the walker the bubble floats above. */
  charId: string;
  kind: 'chat' | 'reaction';
  text: string;
  /** ms epoch when the bubble should disappear. */
  until: number;
}

const CHAT_BUBBLE_MS = 6000;
const REACTION_BUBBLE_MS = 2200;

interface Props {
  players: Player[];
  /** Chat messages from useLobbySocket — bubbles spawn for new entries. */
  chat?: ChatMsg[];
  /** Reactions from useLobbySocket — single-glyph bubbles. */
  reactions?: Reaction[];
  /** Wired to sock.sendChat — composer pill submit handler. */
  onSendChat?: (text: string) => void;
  /** Wired to sock.sendReaction — emoji picker. */
  onSendReaction?: (emoji: string) => void;
  /** Reaction-picker emoji set, per the lobby's unlocked list. */
  reactionEmojis?: string[];
  /** When false, hide the composer (chat disabled in this lobby). */
  chatEnabled?: boolean;
  onInspectPlayer?: (charId: string) => void;
  /** Local player's charId — shown with a "me" rim. */
  meCharId?: string;
}

export function TavernFloor({
  players, chat, reactions, onSendChat, onSendReaction,
  reactionEmojis, chatEnabled = true, onInspectPlayer, meCharId,
}: Props) {
  // Stable per-player simulation state. Re-seeded only when the player
  // roster changes (join / leave).
  const sims = useMemo<AvatarSim[]>(() => players.map((p) => {
    const h = hashId(p.charId);
    const spot = SPOTS[h % SPOTS.length]!;
    return {
      player: p,
      x: spot.x, y: spot.y,
      targetX: spot.x, targetY: spot.y,
      dwell: 4 + (h % 5),
      hash: h,
      facingFlip: 1,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [players.length, players.map((p) => p.charId).join(',')]);

  // DOM refs — one per walker. Position is mutated each frame in rAF
  // without a React re-render. Inner-icon ref is the one that gets the
  // facing flip — the outer button stays unflipped so the nameplate
  // never reads backwards (per docs/ui/tavern-floor-chat.md §3.1).
  const walkerRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const iconRefs   = useRef(new Map<string, HTMLSpanElement | null>());

  // Speech bubbles + reaction bubbles. Keyed by bubble.id so multiple
  // can stack per walker. Bubbles auto-expire by `until`.
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  // Track which chat / reaction items we've already turned into
  // bubbles so reconnects / re-renders don't spawn duplicates.
  const lastChatKeyRef = useRef<string>('');
  const lastReactionIdRef = useRef<number>(0);
  // Per-charId timestamp of the LAST bubble we spawned, to rate-limit
  // bubble spawns to one per 3s per walker. Without this, rapid
  // chat/reaction bursts produce stacked bubbles that visually fight
  // for space and trigger the wrap-into-3-letter-rows bug.
  const lastBubbleAtRef = useRef<Map<string, number>>(new Map());
  const BUBBLE_RATE_LIMIT_MS = 3000;

  // Composer state.
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Spawn bubbles for new chat messages.
  useEffect(() => {
    if (!chat || chat.length === 0) return;
    const newest = chat[chat.length - 1]!;
    const key = `${newest.at}-${newest.charId}-${newest.text.slice(0, 16)}`;
    if (key === lastChatKeyRef.current) return;
    lastChatKeyRef.current = key;
    // Rate-limit per-walker so spammy chat bursts don't stack 3+ bubbles
    // that visually compete and break their layout.
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()),
      { id, charId: newest.charId, kind: 'chat', text: newest.text.slice(0, 80), until: Date.now() + CHAT_BUBBLE_MS },
    ]);
  }, [chat]);

  // Spawn bubbles for new reactions.
  useEffect(() => {
    if (!reactions || reactions.length === 0) return;
    const newest = reactions[reactions.length - 1]!;
    if (newest.id === lastReactionIdRef.current) return;
    lastReactionIdRef.current = newest.id;
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `r-${newest.id}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()),
      { id, charId: newest.charId, kind: 'reaction', text: newest.emoji, until: Date.now() + REACTION_BUBBLE_MS },
    ]);
  }, [reactions]);

  // Periodically GC expired bubbles.
  useEffect(() => {
    if (bubbles.length === 0) return;
    const t = window.setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.until > Date.now()));
    }, 600);
    return () => window.clearTimeout(t);
  }, [bubbles]);

  // rAF walker loop.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(50, now - last) / 1000;
      last = now;
      for (const a of sims) {
        const k = Math.min(1, dt * 0.6);
        a.x += (a.targetX - a.x) * k;
        a.y += (a.targetY - a.y) * k;
        const dx = a.targetX - a.x;
        const newFlip: 1 | -1 = Math.abs(dx) > 0.5 ? (dx < 0 ? -1 : 1) : a.facingFlip;
        a.facingFlip = newFlip;
        a.dwell -= dt;
        if (a.dwell <= 0) {
          a.hash = (a.hash * 1103515245 + 12345) & 0x7fffffff;
          const spot = SPOTS[a.hash % SPOTS.length]!;
          a.targetX = spot.x + ((a.hash >> 4) % 8) - 4;
          a.targetY = spot.y + ((a.hash >> 8) % 6) - 3;
          a.dwell = 5 + (a.hash % 7);
        }
        // Drive DOM directly. The OUTER button moves but never flips;
        // the INNER icon span owns the scaleX flip so the nameplate
        // text doesn't render backwards when walking left.
        const el = walkerRefs.current.get(a.player.charId);
        if (el) {
          el.style.left = `${a.x}%`;
          el.style.top = `${a.y}%`;
        }
        const iconEl = iconRefs.current.get(a.player.charId);
        if (iconEl) {
          iconEl.style.transform = `scaleX(${newFlip})`;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sims]);

  if (players.length === 0) return null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || !onSendChat) return;
    onSendChat(t);
    setDraft('');
  };

  const reactionPicks = (reactionEmojis && reactionEmojis.length > 0)
    ? reactionEmojis
    : ['👏', '🔥', '💀', '🎉', '😂', '😱'];

  return (
    <section className="tavern-floor" aria-label="players in the tavern">
      <div className="tavern-floor-board">
        {/* Richer top-down SVG floor — wood planks with knots + grain,
         *  hearth glow circle, rug rectangle, table outlines, soft
         *  vignette. Rendered absolutely behind every other floor child
         *  (spots, walkers, bubbles) so it doesn't interfere with
         *  pointer events. 2026-05-16. */}
        <TavernFloorSvg />
        {SPOTS.map((s) => (
          <span
            key={s.id}
            className="tavern-floor-spot"
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
            title={s.label}
          >
            <span aria-hidden="true">{s.emoji}</span>
          </span>
        ))}
        {sims.map((a) => {
          const myBubbles = bubbles.filter((b) => b.charId === a.player.charId).slice(-3);
          const isMe = meCharId === a.player.charId;
          const isHost = !!a.player.isHost;
          const isReady = !!a.player.ready;
          const isDisco = !!a.player.isDisconnected;
          return (
            <button
              type="button"
              key={a.player.charId}
              ref={(el) => { walkerRefs.current.set(a.player.charId, el); }}
              className={`tavern-floor-walker${isMe ? ' is-me' : ''}${isReady ? ' is-ready' : ''}${isHost ? ' is-host' : ''}${isDisco ? ' is-disco' : ''}`}
              style={{
                left: `${a.x}%`,
                top: `${a.y}%`,
              }}
              onClick={() => onInspectPlayer?.(a.player.charId)}
              aria-label={`${a.player.name}${isReady ? ' (ready)' : ''}${isHost ? ' (host)' : ''}${isDisco ? ' (disconnected)' : ''}`}
            >
              {/* Speech / reaction bubbles, stacked newest-up. */}
              {myBubbles.length > 0 && (
                <span className="tavern-floor-bubble-stack" aria-hidden="true">
                  {myBubbles.map((b, i) => (
                    <span
                      key={b.id}
                      className={`tavern-floor-bubble tavern-floor-bubble-${b.kind}`}
                      style={{ ['--stack-i' as string]: String(myBubbles.length - 1 - i) }}
                    >
                      {b.text}
                    </span>
                  ))}
                </span>
              )}
              {/* Inner icon — this is what flips when walking left. */}
              <span
                className="tavern-floor-walker-icon"
                ref={(el) => { iconRefs.current.set(a.player.charId, el); }}
              >
                <IconAvatar icon={a.player.icon} size={48} alt="" />
                {isHost && <span className="tavern-floor-crown" aria-hidden="true">♛</span>}
                {isReady && <span className="tavern-floor-tick" aria-hidden="true">✓</span>}
              </span>
              <span className="tavern-floor-name">
                <strong>{a.player.name}</strong>
                {a.player.title && a.player.title !== 'Tavern Regular' && (
                  <small className={titleRarityClass(a.player.title)}>{a.player.title}</small>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Composer pill docked to the bottom of the floor.
          Always visible — no scroll required on mobile. */}
      {chatEnabled && (
        <form className={`tavern-floor-composer${historyOpen ? ' history-open' : ''}`} onSubmit={onSubmit}>
          <button
            type="button"
            className="tavern-floor-composer-history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
          >
            {historyOpen ? '▼' : '▲'}
          </button>
          {historyOpen && chat && chat.length > 0 && (
            <div className="tavern-floor-history" role="log" aria-live="polite">
              {chat.slice(-12).map((m, i) => (
                <div key={i} className="tavern-floor-history-row">
                  <span className="tavern-floor-history-name">{m.name}:</span> <span>{m.text}</span>
                </div>
              ))}
            </div>
          )}
          <input
            type="text"
            className="tavern-floor-composer-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="say something…"
            maxLength={200}
            aria-label="Chat message"
          />
          <button
            type="button"
            className="tavern-floor-composer-react"
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="Send a reaction"
            aria-expanded={pickerOpen}
          >
            ☺
          </button>
          {pickerOpen && (
            <div className="tavern-floor-react-picker" role="dialog" aria-label="Reactions">
              {reactionPicks.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="tavern-floor-react-glyph"
                  onClick={() => { onSendReaction?.(e); setPickerOpen(false); }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
          <button
            type="submit"
            className="tavern-floor-composer-send"
            disabled={!draft.trim()}
            aria-label="Send"
          >
            <span className="tavern-floor-composer-seal">✉</span>
          </button>
        </form>
      )}
      <div className="tavern-floor-tagline">at the tavern · tap a regular to peek</div>
    </section>
  );
}

function hashId(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * Top-down tavern floor — rendered behind the spot markers and walkers
 * inside `.tavern-floor-board`. Replaces the flat CSS-gradient floor
 * with an illustrated SVG: horizontal wood planks with knots + grain
 * highlights, a circular hearth glow at the north, a wool rug centered
 * on the floor, the bar counter strip along the east, low table
 * outlines, and a soft inner vignette. ViewBox 720×220 matches the
 * `.tavern-floor-board` aspect; SVG uses `preserveAspectRatio="none"`
 * so it stretches with the board container at every width.
 */
function TavernFloorSvg() {
  return (
    <svg
      className="tavern-floor-svg"
      viewBox="0 0 720 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        borderRadius: 'inherit',
        zIndex: 0,
      }}
    >
      <defs>
        <pattern id="tfPlank" x="0" y="0" width="240" height="42" patternUnits="userSpaceOnUse">
          <rect width="240" height="42" fill="#1d100a" />
          <rect x="0" y="0"  width="240" height="21" fill="#2b1a10" />
          <rect x="0" y="21" width="240" height="21" fill="#26160c" />
          <line x1="0" y1="0"  x2="240" y2="0"  stroke="#0a0604" strokeWidth="1.2" />
          <line x1="0" y1="21" x2="240" y2="21" stroke="#0a0604" strokeWidth="1.2" />
          <line x1="0" y1="2"  x2="240" y2="2"  stroke="#5b3a1f" strokeWidth="0.5" opacity="0.45" />
          <line x1="0" y1="23" x2="240" y2="23" stroke="#5b3a1f" strokeWidth="0.5" opacity="0.4" />
          {/* Staggered end-cap board breaks */}
          <line x1="78"  y1="0"  x2="78"  y2="21" stroke="#0a0604" strokeWidth="0.9" />
          <line x1="172" y1="21" x2="172" y2="42" stroke="#0a0604" strokeWidth="0.9" />
          {/* Knots */}
          <circle cx="40"  cy="10" r="1.8" fill="#0a0604" />
          <circle cx="160" cy="32" r="1.6" fill="#0a0604" />
          <circle cx="210" cy="14" r="1.4" fill="#0a0604" />
        </pattern>
        <radialGradient id="tfHearthGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="#ffd28a" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#ff8c42" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ff4a2a" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tfRug" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="#7a1e2a" stopOpacity="0.9" />
          <stop offset="80%" stopColor="#3a1018" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#2a0c10" stopOpacity="0.8" />
        </radialGradient>
        <radialGradient id="tfVignette" cx="0.5" cy="0.5" r="0.7">
          <stop offset="60%"  stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.55" />
        </radialGradient>
      </defs>

      {/* Plank floor */}
      <rect x="0" y="0" width="720" height="220" fill="url(#tfPlank)" />

      {/* Hearth glow circle at the top (matches the 🔥 spot at ~50% / 22%) */}
      <ellipse cx="360" cy="48" rx="120" ry="46" fill="url(#tfHearthGlow)" />
      {/* Inner hearth disc — masonry hint */}
      <ellipse cx="360" cy="48" rx="42" ry="18" fill="#241420" stroke="#0a0608" strokeWidth="1.5" />
      <ellipse cx="360" cy="50" rx="24" ry="10" fill="#ff6a2a" opacity="0.55" />
      <ellipse cx="360" cy="50" rx="14" ry="6"  fill="#ffd28a" opacity="0.85" />

      {/* Wool rug — centered, oval, dark crimson */}
      <ellipse cx="360" cy="120" rx="210" ry="62" fill="url(#tfRug)" opacity="0.7" />
      {/* Rug border stripe */}
      <ellipse cx="360" cy="120" rx="208" ry="60" fill="none" stroke="#cab8a0" strokeWidth="1" opacity="0.4" strokeDasharray="3 5" />
      {/* Rug center medallion */}
      <ellipse cx="360" cy="120" rx="42" ry="14" fill="none" stroke="#cab8a0" strokeWidth="0.8" opacity="0.35" />
      <ellipse cx="360" cy="120" rx="22" ry="8"  fill="none" stroke="#cab8a0" strokeWidth="0.7" opacity="0.3" />

      {/* Bar counter strip along the right edge (matches the 🍻 spot) */}
      <g opacity="0.85">
        <rect x="60" y="48" width="86" height="42" fill="#3a2410" stroke="#0a0604" strokeWidth="1" rx="3" />
        <rect x="60" y="48" width="86" height="3"  fill="#a87a3a" opacity="0.55" />
        <line x1="60" y1="60" x2="146" y2="60" stroke="#1a0e08" strokeWidth="0.5" opacity="0.6" />
        <line x1="60" y1="80" x2="146" y2="80" stroke="#1a0e08" strokeWidth="0.5" opacity="0.5" />
      </g>

      {/* Window ledge silhouette at top-right (matches the 🌙 spot) */}
      <g opacity="0.7">
        <rect x="570" y="40" width="110" height="46" fill="#1a1218" stroke="#0a0608" strokeWidth="1" rx="3" />
        <rect x="576" y="46" width="48" height="34" fill="#2a3045" opacity="0.55" />
        <rect x="628" y="46" width="48" height="34" fill="#2a3045" opacity="0.55" />
        {/* Cross-pane mullion */}
        <line x1="600" y1="46" x2="600" y2="80" stroke="#0a0608" strokeWidth="0.8" />
        <line x1="652" y1="46" x2="652" y2="80" stroke="#0a0608" strokeWidth="0.8" />
        <line x1="570" y1="63" x2="680" y2="63" stroke="#0a0608" strokeWidth="0.6" opacity="0.6" />
      </g>

      {/* Billiards-table / corner outlines (light hints under the
       *  bottom two spots at 32% / 70% and 70% / 75%) */}
      <g opacity="0.4">
        <rect x="180" y="138" width="80" height="36" fill="none" stroke="#5b3a1f" strokeWidth="1.4" rx="4" />
        <rect x="186" y="144" width="68" height="24" fill="#1d6a4a" opacity="0.55" />
        <ellipse cx="490" cy="170" rx="48" ry="14" fill="none" stroke="#5b3a1f" strokeWidth="1.4" />
      </g>

      {/* Soft inner vignette so the rug + spots draw eye to center */}
      <rect x="0" y="0" width="720" height="220" fill="url(#tfVignette)" />
    </svg>
  );
}
